import { Link } from "react-router-dom";
import { useMemo } from "react";
import type { Opportunity, Pipeline, Task } from "@sales-ops-amplify/shared";
import { taskTypeToHubTab } from "@sales-ops-amplify/shared";
import { useAuth } from "@/hooks/useAuth";
import { useOpportunities } from "@/hooks/useOpportunities";
import { usePipelines } from "@/hooks/usePipeline";
import { useTasks } from "@/hooks/useTasks";
import { computePipelineAnalytics, getPipelineForJob } from "@/lib/pipeline-analytics";
import { SalesCommandHome } from "@/components/sales-command";
import { cn } from "@/cn";

export function HomePage() {
  return (
    <SalesCommandHome OperationalHomeFallback={OperationalHomeFallback} />
  );
}

const STUCK_MS = 14 * 24 * 60 * 60 * 1000;
const CLOSED_STAGES = new Set(["Won", "Lost", "Passed"]);

type AttentionReason = "Overdue" | "Unprocessed" | "Stuck";

function attentionReasonRank(reason: AttentionReason): number {
  if (reason === "Overdue") return 0;
  if (reason === "Unprocessed") return 1;
  return 2;
}

function normalizeProbability(probability: number | undefined): number | undefined {
  if (probability === undefined || Number.isNaN(probability)) return undefined;
  if (probability > 1) return Math.min(probability / 100, 1);
  return Math.max(probability, 0);
}

function computeWeightedPipeline(jobs: Opportunity[]): number | null {
  let sum = 0;
  let any = false;
  for (const job of jobs) {
    if (typeof job.amount !== "number" || Number.isNaN(job.amount)) continue;
    const p = normalizeProbability(job.probability);
    if (p === undefined) continue;
    sum += job.amount * p;
    any = true;
  }
  return any ? sum : null;
}

function formatMoney(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isOverdue(iso: string): boolean {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return false;
  return due < startOfDay(new Date());
}

function buildNeedsAttention(
  jobs: Opportunity[],
  pipelines: Pipeline[],
  limit = 5,
): Array<{ job: Opportunity; reasons: AttentionReason[] }> {
  const now = Date.now();
  const items: Array<{ job: Opportunity; reasons: AttentionReason[]; rank: number }> = [];

  for (const job of jobs) {
    const reasons: AttentionReason[] = [];
    const pipeline = getPipelineForJob(job, pipelines);
    const inPipeline = Boolean(job.pipelineId || pipeline);

    if (job.nextStepDueAt) {
      const due = new Date(job.nextStepDueAt).getTime();
      if (!Number.isNaN(due) && due < now) reasons.push("Overdue");
    }

    if (!inPipeline) reasons.push("Unprocessed");

    const lastUpdateRaw = job.updatedAt || job.lastUpdated || job.createdAt;
    const lastUpdate = lastUpdateRaw ? new Date(lastUpdateRaw).getTime() : NaN;
    const stage = pipeline?.currentStage;
    if (
      inPipeline &&
      !Number.isNaN(lastUpdate) &&
      now - lastUpdate >= STUCK_MS &&
      (!stage || !CLOSED_STAGES.has(stage))
    ) {
      reasons.push("Stuck");
    }

    if (reasons.length === 0) continue;
    items.push({
      job,
      reasons,
      rank: Math.min(...reasons.map(attentionReasonRank)),
    });
  }

  return items
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const aDue = a.job.nextStepDueAt ? new Date(a.job.nextStepDueAt).getTime() : Infinity;
      const bDue = b.job.nextStepDueAt ? new Date(b.job.nextStepDueAt).getTime() : Infinity;
      return aDue - bDue;
    })
    .slice(0, limit)
    .map(({ job, reasons }) => ({ job, reasons }));
}

/** Flag-off fallback: tasks + needs-attention pulse — not the Sales Dashboard. */
function OperationalHomeFallback() {
  const { user } = useAuth();
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useOpportunities();
  const { data: pipelinesData, isLoading: pipelinesLoading } = usePipelines();
  const { data: tasksData, isLoading: tasksLoading } = useTasks({
    status: "open",
    sort: "due",
    limit: 8,
  });

  const jobs = jobsData?.opportunities ?? [];
  const pipelines = pipelinesData?.pipelines ?? [];
  const homeTasks = tasksData?.tasks ?? [];

  const isLoading = jobsLoading || pipelinesLoading || tasksLoading;

  const stats = useMemo(
    () => computePipelineAnalytics(jobs, pipelines),
    [jobs, pipelines],
  );

  const weightedPipeline = useMemo(() => computeWeightedPipeline(jobs), [jobs]);

  const needsAttention = useMemo(
    () => buildNeedsAttention(jobs, pipelines, 5),
    [jobs, pipelines],
  );

  return (
    <>
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Home</h1>
        <p className="mt-1 text-muted-foreground">
          Welcome back{user?.name ? `, ${user.name}` : ""}. Your tasks and deals
          that need attention today.
        </p>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
            <p className="text-sm text-muted-foreground">Loading…</p>
          </div>
        </div>
      )}

      {jobsError && !isLoading && (
        <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load data. Please try again later.
        </div>
      )}

      {!isLoading && !jobsError && (
        <>
          <div className="mb-6 flex flex-wrap gap-2">
            <Link
              to="/conversations"
              className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90"
            >
              Conversations
            </Link>
            <Link
              to="/deals/add"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted/20"
            >
              Add deal
            </Link>
            <Link
              to="/people"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted/20"
            >
              Find people
            </Link>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Your tasks</h2>
            <Link to="/tasks" className="text-sm text-brand hover:underline">
              View all tasks
            </Link>
          </div>

          {homeTasks.length === 0 ? (
            <div className="mb-8 rounded-lg border border-border bg-card p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No open tasks.{" "}
                <Link to="/tasks" className="text-brand hover:underline">
                  Create a task
                </Link>{" "}
                or run a{" "}
                <Link to="/sequences" className="text-brand hover:underline">
                  sequence
                </Link>
                .
              </p>
            </div>
          ) : (
            <div className="mb-8 min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/20">
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Task</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Company</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {homeTasks.map((task: Task) => {
                    const tab = taskTypeToHubTab(task.type);
                    const href = `/tasks?tab=${tab}`;
                    const overdue = isOverdue(task.dueAt);
                    return (
                      <tr
                        key={task.taskId}
                        className="border-b border-border last:border-0 hover:bg-muted/10"
                      >
                        <td className="px-4 py-2.5">
                          <Link to={href} className="font-medium text-foreground hover:text-brand">
                            {task.title}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">{task.company || "—"}</td>
                        <td className="px-4 py-2.5">
                          <span
                            className={cn(
                              "text-xs font-medium",
                              overdue ? "text-red-400" : "text-muted-foreground",
                            )}
                          >
                            {overdue
                              ? "Past due"
                              : new Date(task.dueAt).toLocaleDateString()}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mb-8 grid gap-6 lg:grid-cols-3">
            <div className="rounded-lg border border-border bg-card p-5 lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Needs attention
                </h2>
                <Link to="/deals" className="text-xs text-brand hover:underline">
                  View deals
                </Link>
              </div>
              {needsAttention.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nothing overdue, unpiped, or stuck. Pipeline looks healthy.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {needsAttention.map(({ job, reasons }) => (
                    <Link
                      key={job.id}
                      to={`/deals/${job.id}`}
                      className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2.5 transition hover:bg-muted/10"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">{job.company}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {job.dealName}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap justify-end gap-1">
                        {reasons.map((reason) => (
                          <span
                            key={reason}
                            className={cn(
                              "rounded px-1.5 py-0.5 text-[10px] font-medium",
                              reason === "Overdue"
                                ? "bg-red-500/15 text-red-400"
                                : reason === "Unprocessed"
                                  ? "bg-amber-500/15 text-amber-400"
                                  : "bg-muted text-muted-foreground",
                            )}
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border bg-card p-5">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Pipeline pulse
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <PulseStat label="Active" value={String(stats.active)} />
                <PulseStat
                  label="Weighted"
                  value={weightedPipeline !== null ? formatMoney(weightedPipeline) : "—"}
                />
                <PulseStat label="Demos" value={String(stats.demos)} />
                <PulseStat label="Won" value={String(stats.won)} />
              </div>
              <Link
                to="/deals"
                className="mt-4 inline-block text-xs text-brand hover:underline"
              >
                Open deals
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function PulseStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/80 px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-semibold text-foreground">{value}</p>
    </div>
  );
}
